import { Rng } from '../rng.js';
import type { WaveSpec } from '../scenario.js';
import { World, type RunConfig } from '../world.js';
import { playReferenceRun } from './reference.js';
import {
  generateMap,
  MAP_H,
  MAP_W,
  rasterize,
  type BuffTileKind,
  type GeneratedMap,
} from './mapgen.js';
import { generateWaves, type WaveGenData } from './wavegen.js';

/**
 * The daily generator (TRD-13).
 *
 * Everything a daily is — layout, waves, mutator — falls out of the seed. The client and
 * the server both call this, and the server never trusts a config the client supplies
 * (TRD-11 R-11-06), so any divergence becomes a rejected submission rather than an exploit.
 */

/** Generation draws from stream 2, gameplay from stream 1 (R-13-04). */
const GEN_STREAM = 2n;

export interface MutatorSpec {
  id: string;
  name: string;
  description: string;
  threatBudgetMultiplier: number;
  weight: number;
}

export interface GenData extends WaveGenData {
  pathLength: readonly [number, number];
  terrainDensity: readonly [number, number];
  /** Buff-tile kinds and how many to scatter — see mapgen's `placeBuffTiles`. */
  buffTileKinds?: readonly BuffTileKind[];
  buffTileCount?: readonly [number, number];
  maxAttempts: number;
  probeTargetWave: number;
  mutators: readonly MutatorSpec[];
}

export interface GeneratedRun {
  map: GeneratedMap;
  waves: WaveSpec[];
  mutator: MutatorSpec | null;
  /** Candidates rejected before this one was accepted — telemetry (R-13-38). */
  attempts: number;
  /** How far the reference player got. Watch its distribution over time (R-13-38). */
  probeWave: number;
  /** True when every candidate failed and the caller must fall back to authored content. */
  exhausted: boolean;
  /**
   * The mutator that had to be dropped to find a playable day, if any.
   *
   * Non-null means that mutator is harder than its threatBudgetMultiplier compensates
   * for. Watch this — it is the signal that a multiplier needs re-deriving (R-13-31).
   */
  droppedMutator: string | null;
}

export interface ValidationIssue {
  rule: string;
  detail: string;
}

// ── Structural checks (§7.1) ──────────────────────────────────────────────────────────

/**
 * How much of the single-lane minimum a lane must still be when a map has two.
 *
 * 0.55 of 45 is 25 tiles: short enough that a second lane actually fits beside a full
 * serpentine, long enough that a tower still gets several seconds of firing on it.
 */
const MULTI_LANE_LENGTH_FLOOR = 0.55;

export function validateMap(
  map: GeneratedMap,
  pathLength: readonly [number, number],
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // The floor drops on a multi-lane map, and this is the whole reason two-lane days were
  // rare (playtest measurement).
  //
  // The generator deliberately walks a SHORTER second lane — a 45-70 tile serpentine has
  // already taken the board and asking for another of the same length asks for something
  // that does not fit — and then this rule threw the result away for being short. Two
  // thirds of every two-lane map that walked successfully died right here, on tile counts
  // like [55, 28].
  //
  // A shorter lane is also the correct answer rather than a tolerated one: enemies are
  // split across the lanes, so each one carries roughly half the traffic, and half the
  // enemies over half the distance is the same pressure per lane.
  const minTiles =
    map.paths.length > 1 ? Math.round(pathLength[0] * MULTI_LANE_LENGTH_FLOOR) : pathLength[0];

  for (const path of map.paths) {
    const tiles = rasterize(path.waypoints);
    if (tiles.length < minTiles || tiles.length > pathLength[1] + 12) {
      issues.push({ rule: 'V-G1', detail: `path ${path.id} is ${tiles.length} tiles` });
    }
    for (const wp of path.waypoints) {
      if (wp.x < 0 || wp.y < 0 || wp.x >= MAP_W || wp.y >= MAP_H) {
        issues.push({ rule: 'V-G6', detail: `path ${path.id} leaves the grid` });
        break;
      }
    }
  }

  let buildable = 0;
  for (const row of map.buildMask) for (const c of row) if (c === '.') buildable++;
  if (buildable < 180) issues.push({ rule: 'V-G2', detail: `only ${buildable} buildable tiles` });

  // V-G3, the load-bearing one. A stretch of path with nothing buildable beside it is a
  // stretch the player cannot defend, and it is the most common way a naive generator
  // emits a map that looks fine and cannot be won.
  const coverage = minPathCoverage(map);
  if (coverage < 6) {
    issues.push({
      rule: 'V-G3',
      detail: `a path tile has only ${coverage} buildable tiles within 3`,
    });
  }

  const spawn = map.spawns[0];
  if (spawn) {
    const dx = map.core.x - spawn.x;
    const dy = map.core.y - spawn.y;
    if (Math.sqrt(dx * dx + dy * dy) < 12) {
      issues.push({ rule: 'V-G4', detail: 'spawn is too close to the Core' });
    }
  }

  return issues;
}

export function minPathCoverage(map: GeneratedMap, radius = 3): number {
  let worst = Number.POSITIVE_INFINITY;
  for (let y = 0; y < MAP_H; y++) {
    const row = map.buildMask[y];
    if (!row) continue;
    for (let x = 0; x < MAP_W; x++) {
      if (row[x] !== 'p') continue;
      let n = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (dx * dx + dy * dy > radius * radius) continue;
          if (map.buildMask[y + dy]?.[x + dx] === '.') n++;
        }
      }
      if (n < worst) worst = n;
    }
  }
  return worst === Number.POSITIVE_INFINITY ? 0 : worst;
}

export function validateWaves(waves: readonly WaveSpec[], data: GenData): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const seen = new Set<string>();

  const lastWave = waves.length > 0 ? waves[waves.length - 1]?.wave : undefined;

  for (const wave of waves) {
    if (wave.groups.length === 0) {
      issues.push({ rule: 'V-G7', detail: `wave ${wave.wave} is empty` });
    }
    if (!wave.isBoss && wave.groups.length > 3) {
      issues.push({
        rule: 'V-G7',
        detail: `wave ${wave.wave} has ${wave.groups.length} archetypes`,
      });
    }
    for (const group of wave.groups) {
      const gate = data.archetypeGates[group.enemy];
      // The finale is exempt on the final wave. Gates govern the DRAW — they stop a
      // wave-25 threat turning up on wave 8 — and the finale is not drawn, it is the
      // fixed ending. Without this, every generated run shorter than Prism's gate
      // rejects itself and falls back to authored content.
      const isFinale = wave.wave === lastWave && group.enemy === data.finalBoss;
      if (gate !== undefined && wave.wave < gate && !isFinale) {
        issues.push({
          rule: 'V-G9',
          detail: `${group.enemy} appears on wave ${wave.wave}, gated to ${gate}`,
        });
      }
      seen.add(group.enemy);
    }
  }

  // Cumulative threat must not go backwards over any 3-wave window (V-G8): a run that
  // gets easier partway through reads as broken even when every individual wave is fine.
  const totals = waves.map((w) =>
    w.groups.reduce((sum, g) => sum + (data.threatCosts[g.enemy] ?? 1) * g.count, 0),
  );
  for (let i = 3; i < totals.length; i++) {
    const prev = (totals[i - 3] ?? 0) + (totals[i - 2] ?? 0) + (totals[i - 1] ?? 0);
    const next = (totals[i - 2] ?? 0) + (totals[i - 1] ?? 0) + (totals[i] ?? 0);
    if (next < prev * 0.7) {
      issues.push({ rule: 'V-G8', detail: `threat drops sharply around wave ${i + 1}` });
      break;
    }
  }

  return issues;
}

// ── Orchestrator ──────────────────────────────────────────────────────────────────────

export interface GenerateOptions {
  data: GenData;
  /** Builds a playable RunConfig from a candidate — supplied by the caller so the sim
   *  never imports content (R-13-02). */
  toRunConfig: (map: GeneratedMap, waves: WaveSpec[], mutator: MutatorSpec | null) => RunConfig;
}

/**
 * Generate, validate, and playtest a daily.
 *
 * The probe is the part that makes generated pacing safe. Structural checks prove a map is
 * *shaped* correctly; only playing it proves it can be won. That is affordable solely
 * because the simulation is headless and deterministic — a probe run costs milliseconds.
 */
export function generateDailyRun(seed: bigint, options: GenerateOptions): GeneratedRun {
  const { data } = options;
  const rng = new Rng(seed, GEN_STREAM);

  // The mutator is rolled ONCE, before the retry loop.
  //
  // Rolling it per attempt made the search drift toward mutators the probe happened to
  // like: across 30 days, three of nine mutators accounted for every result and six never
  // appeared at all. The day's modifier must be a property of the seed, not a side effect
  // of how many map candidates were rejected.
  const rolled = rollMutator(rng, data.mutators);

  /**
   * Two passes: with the day's mutator, then without it.
   *
   * Some mutators are simply harder than their `threatBudgetMultiplier` claims to
   * compensate for — Austerity's lost placement, in particular, costs the reference
   * player far more than a 20% budget cut returns. Once the mutator stopped being
   * rerolled on every retry, those seeds could not find an acceptable map at all and
   * 44% of days fell through to authored content.
   *
   * Dropping the modifier keeps the generated map, which is much less of a loss than
   * dropping the whole day. A day that lands here is also a signal that the mutator's
   * multiplier needs re-deriving — `droppedMutator` surfaces it rather than hiding it.
   */
  for (const mutator of [rolled, null]) {
    const budgetScale = mutator?.threatBudgetMultiplier ?? 1;

    for (let attempt = 0; attempt < data.maxAttempts; attempt++) {
      const map = generateMap(rng, {
        pathLength: data.pathLength,
        terrainDensity: data.terrainDensity,
        ...(data.buffTileKinds !== undefined ? { buffTileKinds: data.buffTileKinds } : {}),
        ...(data.buffTileCount !== undefined ? { buffTileCount: data.buffTileCount } : {}),
      });
      if (!map) continue;
      if (validateMap(map, data.pathLength).length > 0) continue;

      const waves = generateWaves(rng, data, budgetScale);
      if (validateWaves(waves, data).length > 0) continue;

      const config = options.toRunConfig(map, waves, mutator);
      const probe = playReferenceRun(new World(seed, config), data.probeTargetWave);

      if (probe.waveReached >= data.probeTargetWave) {
        return {
          map,
          waves,
          mutator,
          attempts: attempt,
          probeWave: probe.waveReached,
          exhausted: false,
          droppedMutator: mutator === null && rolled !== null ? rolled.id : null,
        };
      }
    }

    if (rolled === null) break;
  }

  // Every candidate failed. The caller falls back to authored content — which exists
  // anyway for normal runs, so the generator gets its safety net for free (R-13-33).
  return {
    map: { paths: [], core: { x: 0, y: 0 }, spawns: [], buildMask: [], blockers: [], buffTiles: [] },
    waves: [],
    mutator: null,
    attempts: data.maxAttempts,
    probeWave: 0,
    exhausted: true,
    droppedMutator: null,
  };
}

function rollMutator(rng: Rng, mutators: readonly MutatorSpec[]): MutatorSpec | null {
  if (mutators.length === 0) return null;
  return rng.weightedPick(
    mutators,
    mutators.map((m) => m.weight),
  );
}

export { generateMap, generateWaves, playReferenceRun, MAP_W, MAP_H };
export type { GeneratedMap, WaveGenData };
